import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { getVMTDataForGHG } from '../../services/vmtDataService';
import './VMTComparisonChart.css';

Chart.register(...registerables);

interface VMTComparisonChartProps {
  location: string;
  geofips: number | null;
}

interface VMTDataPoint {
  year: number;
  ldv: number;
  hdv: number;
  total: number;
}

export const VMTComparisonChart: React.FC<VMTComparisonChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VMTDataPoint[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!location || !geofips) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch LDV VMT directly from the VMT chart's global data (includes user modifications)
        console.log('🔍 VMT Comparison: Fetching LDV VMT from VMT chart data');
        const vmtChartData = await getVMTDataForGHG(location, geofips || undefined);
        console.log('✅ VMT Comparison: LDV VMT data from VMT chart:', vmtChartData);
        
        // Get HDV VMT from emfac_final_estimation
        console.log('🔍 VMT Comparison: Fetching HDV data for geofips:', geofips);
        const { data: hdvData, error: hdvError } = await supabaseClient
          .from('emfac_final_estimation')
          .select('year, total_vmt, geofips')
          .eq('geofips', geofips)
          .eq('class', 'HDV')
          .gte('year', 2005)
          .lte('year', 2023)
          .order('year', { ascending: true });

        console.log('📊 VMT Comparison: HDV data count:', hdvData?.length);
        if (hdvData && hdvData.length > 0) {
          console.log('📊 VMT Comparison: First HDV row:', JSON.stringify(hdvData[0], null, 2));
          console.log('📊 VMT Comparison: Sample HDV rows (first 5):', hdvData.slice(0, 5).map(d => ({ year: d.year, total_vmt: d.total_vmt, geofips: d.geofips })));
          const uniqueYears = Array.from(new Set(hdvData.map(d => d.year)));
          console.log('📊 VMT Comparison: Unique years in HDV data:', uniqueYears.length, uniqueYears.sort());
          
          // Log sum of total_vmt by year to confirm aggregation
          uniqueYears.forEach(year => {
            const yearData = hdvData.filter(d => d.year === year);
            const sum = yearData.reduce((acc, d) => acc + (d.total_vmt || 0), 0);
            console.log(`📊 VMT Comparison: HDV ${year}: ${yearData.length} entries, sum=${sum}`);
          });
        }

        if (hdvError) {
          console.error('Error fetching HDV data:', hdvError);
          throw hdvError;
        }

        // Process HDV data by year
        const hdvByYear = new Map<number, number>();
        
        // Sum HDV VMT for 2015-2023 from emfac_final_estimation
        (hdvData || []).forEach(row => {
          hdvByYear.set(row.year, (hdvByYear.get(row.year) || 0) + (row.total_vmt || 0));
        });
        
        console.log('📊 VMT Comparison: HDV by year (2015-2023):', Array.from(hdvByYear.entries()).map(([year, vmt]) => ({ year, vmt })));
        
        // Get 2015 HDV VMT as baseline for backcasting
        const hdv2015 = hdvByYear.get(2015) || 0;
        console.log('📊 VMT Comparison: HDV 2015 baseline:', hdv2015);
        
        // Resolve county name for EMFAC backcasting ratios
        await supabaseLocationService.initialize().catch(() => {});
        const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
        const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
        const countyVariations = Array.from(new Set([
          `${baseCountyName} County`,
          baseCountyName,
          resolvedCounty
        ]));

        let emfacHdvRatios: { year: number; backcasting_ratio: number }[] | null = null;
        let emfacError: any = null;
        for (const locName of countyVariations) {
          const { data, error } = await supabaseClient
            .from('emfac_county_filtered')
            .select('year, backcasting_ratio')
            .eq('location_name', locName)
            .eq('class', 'HDV')
            .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
            .order('year', { ascending: true });
          if (!error && data && data.length > 0) {
            emfacHdvRatios = data as any;
            console.log('📊 VMT Comparison: Found EMFAC HDV ratios for', locName, 'count:', data.length);
            break;
          }
          emfacError = error;
        }
        console.log('📊 VMT Comparison: EMFAC HDV backcasting ratios count:', emfacHdvRatios?.length || 0);
        if (!emfacHdvRatios && emfacError) {
          console.warn('📊 VMT Comparison: EMFAC HDV backcasting data not available:', emfacError);
        }
        
        // Backcast HDV for 2005-2014 using the ratios
        if (emfacHdvRatios && emfacHdvRatios.length > 0) {
          emfacHdvRatios.forEach(record => {
            if (record.backcasting_ratio) {
              const backcastedVmt = hdv2015 * record.backcasting_ratio;
              hdvByYear.set(record.year, backcastedVmt);
              console.log(`📊 VMT Comparison: HDV backcasting ${record.year}: ratio=${record.backcasting_ratio.toFixed(4)}, vmt=${backcastedVmt.toFixed(0)}`);
            }
          });
        }
        
        // Validate and smooth HDV VMT data: cap year-over-year changes at 50%
        // If change > 50%, assume no change (use previous year's value)
        const hdvYears = Array.from(hdvByYear.keys()).sort((a, b) => a - b);
        const smoothedHdvByYear = new Map<number, number>();
        
        for (let i = 0; i < hdvYears.length; i++) {
          const year = hdvYears[i];
          const currentValue = hdvByYear.get(year) || 0;
          
          if (i === 0) {
            // First year - use as-is
            smoothedHdvByYear.set(year, currentValue);
          } else {
            const previousYear = hdvYears[i - 1];
            const previousValue = smoothedHdvByYear.get(previousYear) || 0;
            
            if (previousValue > 0) {
              const percentChange = Math.abs((currentValue - previousValue) / previousValue);
              
              if (percentChange > 0.5) {
                // Change > 50% - use previous year's value instead
                smoothedHdvByYear.set(year, previousValue);
                console.warn(`⚠️ VMT Comparison: HDV VMT anomaly detected for ${location} ${year}: ${currentValue.toLocaleString()} (${(percentChange * 100).toFixed(1)}% change). Using previous year value: ${previousValue.toLocaleString()}`);
              } else {
                smoothedHdvByYear.set(year, currentValue);
              }
            } else {
              // Previous value is zero - use current value
              smoothedHdvByYear.set(year, currentValue);
            }
          }
        }
        
        // Replace original map with smoothed data
        hdvByYear.clear();
        smoothedHdvByYear.forEach((value, year) => {
          hdvByYear.set(year, value);
        });
        
        console.log('📊 VMT Comparison: HDV by year (smoothed):', Array.from(hdvByYear.entries()).map(([year, vmt]) => ({ year, vmt })));
        
        // Process data using yearMap
        const yearMap = new Map<number, { ldv?: number; hdv?: number }>();
        
        // Add LDV VMT data from the VMT chart (includes user modifications)
        vmtChartData.forEach(vmtRow => {
          yearMap.set(vmtRow.year, { ldv: vmtRow.vmt || 0 });
        });
        
        // Add HDV data (now includes backcasted data for 2005-2014)
        hdvByYear.forEach((vmt, year) => {
          if (!yearMap.has(year)) {
            yearMap.set(year, {});
          }
          const yearData = yearMap.get(year)!;
          yearData.hdv = vmt;
        });
        
        // Log the yearMap before conversion
        console.log('📊 VMT Comparison: yearMap entries:', Array.from(yearMap.entries()).map(([year, values]) => ({ year, ldv: values.ldv, hdv: values.hdv })));

        // Convert to array and sort by year
        const processedData: VMTDataPoint[] = Array.from(yearMap.entries())
          .map(([year, values]) => ({
            year,
            ldv: values.ldv || 0,
            hdv: values.hdv || 0,
            total: (values.ldv || 0) + (values.hdv || 0)
          }))
          .sort((a, b) => a.year - b.year);
        
        console.log('📊 VMT Comparison: Processed data:', processedData);

        setData(processedData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching VMT comparison data:', err);
        setError('Failed to load VMT comparison data');
        setLoading(false);
      }
    };

    fetchData();
  }, [location, geofips]);

  useEffect(() => {
    if (data.length === 0 || !chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create new chart
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.year.toString()),
        datasets: [
          {
            label: 'Light-Duty Vehicles (LDV)',
            data: data.map(d => d.ldv),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(59, 130, 246)',
            pointBorderColor: 'rgb(59, 130, 246)'
          },
          {
            label: 'Heavy-Duty Vehicles (HDV)',
            data: data.map(d => d.hdv),
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(239, 68, 68)',
            pointBorderColor: 'rgb(239, 68, 68)'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          title: {
            display: true,
            text: 'Light-Duty vs Heavy-Duty VMT (2005-2023)'
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.parsed.y;
                return `${context.dataset.label}: ${value.toLocaleString()} miles`;
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Year'
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'VMT (miles)'
            },
            ticks: {
              callback: function(value) {
                return value.toLocaleString();
              }
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  if (loading) {
    return <div className="vmt-comparison-loading">Loading VMT comparison data...</div>;
  }

  if (error) {
    return <div className="vmt-comparison-error">{error}</div>;
  }

  if (data.length === 0) {
    return <div className="vmt-comparison-no-data">No VMT comparison data available</div>;
  }

  return (
    <div className="vmt-comparison-chart-container">
      <canvas ref={chartRef}></canvas>
    </div>
  );
};